import { Box, Typography, useMediaQuery } from '@mui/material';
import Decimal from 'decimal.js';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BasketProductDto } from 'src/api/basketService/dto/BasketProduct.dto';
import { CustomSnackbar } from 'src/components/snackbar/snackbar';
import { routes } from 'src/constants/routes';
import { BookingDetails } from 'src/features/booking-summary/booking-details';
import { PaymentPlan } from 'src/features/payment-plan';
import { RoomCard } from 'src/features/room-card';
import { StepEnum } from 'src/features/step-list/constants';
import { useGetIsValidRebooker } from 'src/lib/hooks/use-get-is-valid-rebooker';
import { useUpdateBasket } from 'src/lib/hooks/use-patch-update-basket';
import { useCalculateBookingDetails } from 'src/lib/hooks/use-post-calculate-booking-details';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import { PathNames } from 'src/shared/enum/PathNames';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import {
    addBasketProduct,
    Basket,
    clearBasket,
    selectBasket,
    selectBasketId,
    selectBasketUser,
    setBasket,
    setBasketError,
    updateBasketOptions,
    updateBasketTracking,
} from 'src/store/slices/basket-slice';
import {
    selectBookingDetails,
    selectRoom,
    setBookingDetails,
    setBookingError,
    setBuildingData,
    setRoomData,
} from 'src/store/slices/booking-slice';
import {
    selectCreatedPaymentPlan,
    selectPromotion,
    selectUserChoices,
    clearPaymentPlan,
} from 'src/store/slices/payment-plan-slice';
import {
    clearSnackbar,
    selectSnackbarState,
    setSnackbarState,
} from 'src/store/slices/snackbar-slice';
import { selectUserLoggedIn } from 'src/store/slices/user-slice';
import store from 'src/store/store';
import { theme } from 'src/styles/theme';
import {
    getAcademicYear,
    getCbl,
    getPaymentPlanId,
    getPromotionId,
    getRebooker,
    getRoomCode,
    setBasketStorage,
    setBookingStorage,
    setStorage,
    updateRedirectState,
} from 'src/utils/local-storage-versioning';
import { isBasketUserValid } from 'src/utils/validators/validate-basket-user';
import { useGetRebookerActive } from 'src/lib/hooks/use-get-rebooker-active';

import { useCreateBasket } from '../../lib/hooks/use-post-create-basket';

import { basketData } from './booking-mock-data';
import { layoutStyles, styles } from './styles';

export const BookingSummaryPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentBasket = useSelector(selectBasket);
    const storedBasketId = useSelector(selectBasketId);
    const basketUserRedux = useSelector(selectBasketUser);
    const roomData = useSelector(selectRoom);
    const { mutateAsync: createBasket } = useCreateBasket();
    const { mutateAsync: updateBasketAsync } = useUpdateBasket();
    const [searchParams, setSearchParams] = useSearchParams();
    const [promotionId, setPromotionId] = useState<string | null>(null);
    const [isRebooker, setIsRebooker] = useState<boolean>(false);
    const [isFillerWeekEligible, setIsFillerWeekEligible] = useState<boolean>(false);
    const [hasDiscount, setHasDiscount] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const isLoggedIn = useSelector(selectUserLoggedIn);

    let isValidBasketUser = false;

    if (basketUserRedux) {
        isValidBasketUser = isBasketUserValid(basketUserRedux);
    }

    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const { mutate: fetchBookingDetails } = useCalculateBookingDetails();
    const { data: isValidRebooker, fetchByRoomCode } = useGetIsValidRebooker();
    const { data: isRebookerActive } = useGetRebookerActive();

    const bookingDetails = useSelector(selectBookingDetails);
    const promotion = useSelector(selectPromotion);
    const userChoices = useSelector(selectUserChoices);
    const paymentPlan = useSelector(selectCreatedPaymentPlan);
    const snackbarState = useSelector(selectSnackbarState);

    const fetchCalculateBookingDetails = (
        roomCode: string,
        academicYear: string,
        paymentPlanId: string | null
    ) => {
        fetchBookingDetails(
            {
                roomName: roomCode,
                academicYear,
                ...(paymentPlanId ? { paymentPlanId } : {}),
            },
            {
                onSuccess: (data) => {
                    dispatch(setRoomData(data.room));
                    dispatch(setBookingDetails(data.bookingDetails));
                    dispatch(setBuildingData(data.building));
                    setBookingStorage({
                        id: null,
                        room: data.room,
                        details: data.bookingDetails,
                        building: data.building,
                        error: null,
                        bookingRecordId: null,
                    });
                },
                onError: () => {
                    dispatch(setBookingError(t('booking_summary_error_fetching_data')));
                },
            }
        );
    };

    useEffect(() => {
        if (roomData && roomData.room_code && isRebookerActive && isLoggedIn) {
            fetchByRoomCode(roomData.room_code);
        }
    }, [roomData, isLoggedIn, isRebookerActive]);

    useEffect(() => {
        if (isValidRebooker && isValidRebooker.rebooker) {
            setPromotionId(isValidRebooker.promotion_id);
            setIsRebooker(isValidRebooker.rebooker);
            setIsFillerWeekEligible(isValidRebooker.filler_week_eligible);
            dispatch(
                updateBasketOptions({
                    rebooker: isValidRebooker.rebooker,
                    rebookerPhase: isValidRebooker.rebookers_stage,
                    fillerWeekEligible: isValidRebooker.filler_week_eligible,
                })
            );
        }
    }, [isValidRebooker]);

    useEffect(() => {
        if (promotion) {
            setHasDiscount(true);
        } else {
            setHasDiscount(false);
        }
    }, [promotion]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [snackbarState.open]);

    useEffect(() => {
        const academicYear = getAcademicYear(searchParams);
        const roomCode = getRoomCode(searchParams);
        const pid = getPromotionId(searchParams);
        const cbl = getCbl(searchParams);
        const rebooker = getRebooker(searchParams);
        const paymentPlanId = getPaymentPlanId(searchParams);

        if (!roomCode || !academicYear) {
            navigate(routes.noRoomSelected.index);
            return;
        }

        if (pid) {
            setPromotionId(pid);
        }

        // Update the redirect state and clear promotion if needed
        updateRedirectState(roomCode, academicYear, pid, cbl, rebooker, paymentPlanId);

        setPromotionId(pid);

        fetchCalculateBookingDetails(roomCode as string, academicYear as string, paymentPlanId);

        const params: {
            roomCode: string;
            academicYear: string;
            pid?: string;
            cbl?: string;
            rebooker?: string;
            paymentPlanId?: string;
        } = {
            roomCode: roomCode as string,
            academicYear: academicYear as string,
            // optionally added to URL
            ...(pid && { pid }),
            ...(cbl && { cbl }),
            ...(rebooker && { rebooker }),
            ...(paymentPlanId && { paymentPlanId }),
        };
        setSearchParams(params);

        setStorage({
            navigationState: {
                page: PathNames.SUMMARY,
                step: StepEnum.YourDetails,
            },
            redirectState: {
                room: roomCode,
                academicYear,
                pid,
                cbl,
                rebooker,
                paymentPlanId,
            },
        });
    }, []);

    if (!roomData || !bookingDetails) return null;
    const currency = bookingDetails.currency as Currency;

    const handleSubmit = async () => {
        if (
            !roomData ||
            !paymentPlan ||
            !paymentPlan.totalRent ||
            !paymentPlan.subtotal ||
            !paymentPlan.paymentOptions ||
            !userChoices.term
        )
            return;
        setLoading(true);

        const academicYear = getAcademicYear(searchParams);
        const roomCode = getRoomCode(searchParams);
        const cbl = getCbl(searchParams);
        const paymentPlanId = getPaymentPlanId(searchParams);

        const product: BasketProductDto = new BasketProductDto({
            id: roomData.id as string,
            room_code: roomData.room_code as string,
            academic_year: roomData.academic_year as string,
            building_id: roomData.building_id as string,
            development_id: roomData.development_slug as string,
            price: new Decimal(roomData.monthly_price ?? roomData.base_price ?? 0),
            currency: roomData.currency as string,
            deposit_prepaid: roomData.deposit_prepaid,
            advance_payment_weeks: roomData.advance_payment_weeks as number,
            beds: roomData.number_of_beds as number,
            deposit: roomData.deposit_prepaid
                ? new Decimal(0)
                : new Decimal(roomData.base_price).times(roomData.advance_payment_weeks),
            selected_students: userChoices.numberOfStudents as number,
            term: userChoices.term.name,
            payment_plan_id: userChoices.instalmentId as string,
            instalment_type: userChoices.instalmentType as string,
            cbl: !!cbl,
            stl: !!paymentPlanId,
            occupants: currentBasket.room_mates || [],
            selected_price: {
                fee: paymentPlan.weeklyPrice,
                today: paymentPlan.paymentOptions[0].price,
                non_discounted: paymentPlan.subtotal,
            },
            legal_representative: null,
            promotion_id: paymentPlan.promotion ? paymentPlan.promotion.id : null,
        });

        try {
            if (storedBasketId && currentBasket.updated_date) {
                const response = await updateBasketAsync({
                    basketId: storedBasketId,
                    basketData: {
                        ...currentBasket,
                        user: null,
                        products: [product],
                    },
                    updatedAt: currentBasket.updated_date,
                });
                dispatch(setBasket(response));
                setBasketStorage(response);
            } else {
                const basket = {
                    ...basketData,
                    user: isValidBasketUser ? basketUserRedux : null,
                    options: currentBasket.options,
                    products: [product],
                } as Basket;
                const response = await createBasket(basket);
                dispatch(setBasket(response));
                setBasketStorage(response);
            }

            // Update Redux store with the new basket data
            dispatch(addBasketProduct(product));
            dispatch(updateBasketTracking(basketData.tracking!));

            // Navigate to the next page with basket data
            navigate(routes.bookingDetails.index);
        } catch (err) {
            if (err.response.code === ErrorCode.ERROR_BASKET_OUTDATED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('basket_out_of_date_message') }));
                return;
            }
            if (err.response.data.code === ErrorCode.PROMOTION_EXPIRED) {
                dispatch(clearBasket());
                dispatch(clearPaymentPlan());
                setPromotionId('');
                setHasDiscount(false);
                dispatch(setSnackbarState({ message: t('promotion_expired_message') }));
                fetchCalculateBookingDetails(
                    roomCode as string,
                    academicYear as string,
                    paymentPlanId
                );
                return;
            }
            if (err.response.data.code === ErrorCode.ALREADY_HAS_BOOKING) {
                setButtonDisabled(true);
                dispatch(
                    setSnackbarState({
                        message: t('user_already_booked', {
                            academicYear: roomData?.academic_year,
                        }),
                    })
                );
            }
            if (err.response.data && err.response.data.data && err.response.data.data.room) {
                if (err.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                    navigate(routes.roomNotAvailable.index);
                    return;
                }
                if (err.response.data.data.room[0] === ValidationErrors.BASE_PRICE_MISMATCH) {
                    fetchCalculateBookingDetails(
                        roomCode as string,
                        academicYear as string,
                        paymentPlanId
                    );
                    dispatch(setSnackbarState({ message: t('room_price_updated_message') }));
                    return;
                }
            }
            store.dispatch(setBasketError(err.error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <CustomSnackbar
                open={snackbarState.open}
                duration={8}
                onClose={() => dispatch(clearSnackbar())}
                message={snackbarState.message}
                isSmallScreen={isSmallScreen}
            />
            <Box css={layoutStyles(hasDiscount)}>
                <Box css={styles.wrapper}>
                    <Typography variant="h1" css={styles.title}>
                        {t('booking_summary_title')}
                    </Typography>
                    <RoomCard hasDiscount={hasDiscount} roomData={roomData} />
                    <BookingDetails
                        bookingDetails={bookingDetails}
                        promotionId={promotionId}
                        isRebooker={isRebooker}
                        isFillerWeekEligible={isFillerWeekEligible}
                        setButtonDisabled={setButtonDisabled}
                    />
                    <PaymentPlan
                        disabled={buttonDisabled}
                        ctaLabel="booking_summary_payment_cta"
                        onSubmit={handleSubmit}
                        currency={currency}
                        loading={loading}
                    />
                </Box>
            </Box>
        </Box>
    );
};
